import { isRouteErrorResponse, Link, useRouteError } from 'react-router-dom';
import { AlertTriangle, RotateCw } from 'lucide-react';
import { Button, buttonVariants } from './Button';

/**
 * Root router errorElement — a styled crash page with a reload CTA, so an
 * unexpected render/loader error never shows the default stack-trace screen.
 */
export function RouteErrorPage() {
  const error = useRouteError();

  let detail = 'An unexpected error occurred while rendering this page.';
  let stack: string | null = null;
  if (isRouteErrorResponse(error)) {
    detail = `${error.status} ${error.statusText}`;
  } else if (error instanceof Error && error.message) {
    detail = error.message;
    // The stack IS the bug report. A crash page that hides it turns every
    // incident into a guessing game (this page hid a #301 for two days);
    // collapsed by default, so nobody reads a trace who didn't ask to.
    stack = error.stack ?? null;
    // Also to the console, where sourcemaps decode it to component + line.
    // eslint-disable-next-line no-console
    console.error('RouteErrorPage caught:', error);
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-grey-50 px-4">
      <div className="card w-full max-w-md p-8 text-center shadow-elevated">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-status-late-bg text-status-late">
          <AlertTriangle className="h-7 w-7" aria-hidden />
        </div>
        <h1 className="text-heading-2 text-secondary">Something went wrong</h1>
        <p className="mx-auto mt-2 max-w-sm break-words text-body-sm text-grey-500">{detail}</p>
        <div className="mt-6 flex flex-col items-center justify-center gap-2 sm:flex-row">
          <Button onClick={() => window.location.reload()}>
            <RotateCw className="h-4 w-4" aria-hidden />
            Reload the page
          </Button>
          <Link to="/dashboard" reloadDocument className={buttonVariants({ variant: 'outline' })}>
            Go to dashboard
          </Link>
        </div>
        {stack && (
          <details className="mt-4 text-left">
            <summary className="cursor-pointer text-caption text-grey-400 hover:text-grey-600">
              Technical details
            </summary>
            <pre className="mt-2 max-h-48 overflow-auto rounded-lg bg-grey-50 p-3 text-left text-[11px] leading-snug text-grey-600">
              {stack}
            </pre>
          </details>
        )}
        <p className="mt-6 text-caption text-grey-400">
          If this keeps happening, screenshot the technical details above.
        </p>
      </div>
    </div>
  );
}
